import type React from 'react'
import { useState } from 'react'
import DefaultModal from '../../../../../components/DefaultModal'
import { useMfaDisableMutation } from '../../../../../redux/services/coreApi'

interface Props {
  isOpen: boolean
  onClose: () => void
  onDisabled: () => void
}

/**
 * Turning MFA off needs the second factor, not just the signed-in session: the API rejects the request
 * unless it carries a current code from the authenticator app or one of the recovery codes. A stolen
 * session therefore can't switch MFA off on its own.
 */
const DisableMfaModal: React.FC<Props> = ({ isOpen, onClose, onDisabled }) => {
  const [disableMfa, { isLoading }] = useMfaDisableMutation()
  const [code, setCode] = useState('')
  const [isUsingRecoveryCode, setIsUsingRecoveryCode] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const reset = () => {
    setCode('')
    setIsUsingRecoveryCode(false)
    setError(null)
  }

  const handleClose = () => {
    if (isLoading) return
    reset()
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError(null)
    const trimmed = code.trim()
    try {
      await disableMfa(
        isUsingRecoveryCode ? { recoveryCode: trimmed } : { totpCode: trimmed }
      ).unwrap()
      reset()
      onDisabled()
    } catch {
      setError(
        isUsingRecoveryCode
          ? 'That recovery code is not valid. Check it and try again.'
          : 'That code is not valid. Enter the current code from your authenticator app.'
      )
    }
  }

  return (
    <DefaultModal
      isOpen={isOpen}
      onClose={handleClose}
      isLoading={isLoading}
      isLoadingText="Disabling MFA…"
      title="Disable MFA"
    >
      <form className="p-5 pt-0 flex flex-col gap-4" onSubmit={handleSubmit}>
        <p className="text-description">
          Your account will be less secure without two-factor authentication. To
          confirm it's you,{' '}
          {isUsingRecoveryCode
            ? 'enter one of the recovery codes you saved when you set up MFA.'
            : 'enter the 6-digit code from your authenticator app.'}
        </p>
        <div className="flex flex-col gap-1">
          <label
            className="text-description text-sm"
            htmlFor="disable-mfa-code"
          >
            {isUsingRecoveryCode ? 'Recovery code' : '6-digit code'}
          </label>
          <input
            id="disable-mfa-code"
            className="input"
            value={code}
            autoComplete="one-time-code"
            inputMode={isUsingRecoveryCode ? 'text' : 'numeric'}
            onChange={e => setCode(e.target.value)}
          />
          {error && <p className="text-sm text-red-500">{error}</p>}
        </div>
        <div>
          <button
            type="button"
            className="text-sm text-blue-500 underline"
            onClick={() => {
              setIsUsingRecoveryCode(!isUsingRecoveryCode)
              setCode('')
              setError(null)
            }}
          >
            {isUsingRecoveryCode
              ? 'Use a code from your authenticator app'
              : 'Use a recovery code'}
          </button>
        </div>
        <div className="flex justify-end gap-3 mt-2">
          <button
            type="button"
            className="button-secondary"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="button-danger"
            disabled={!code.trim() || isLoading}
          >
            Disable MFA
          </button>
        </div>
      </form>
    </DefaultModal>
  )
}

export default DisableMfaModal
